"use client";

import React, { useState, useEffect } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { roundMonetaryValue, formatMonetaryValue } from '@vaiu/utils/currency';
import { formatAmountForCurrency, isMmMoneyCurrency } from '@vaiu/utils/mmMoney';
import { useRedeemUx } from './RedeemUxContext';
import { FormattedCurrencyAmount, RedeemMmMoneyIssuerBanner } from './RedeemCurrencyDisplay';

export interface ManualRedemptionUIProps {
  tokenKey: string;
  tokenType: 'cash' | 'invoice';
  amount: number;
  currency: string;
  message?: string;
  conditions?: unknown;
  onComplete: (result: unknown) => void;
  onCancel: () => void;
  transactionFee?: number;
  netAmount?: number;
}

const ManualRedemptionUI: React.FC<ManualRedemptionUIProps> = ({
  tokenKey,
  tokenType: _tokenType,
  amount,
  currency,
  message,
  conditions: _conditions,
  onComplete,
  onCancel,
  transactionFee = 0,
  netAmount: netAmountProp,
}) => {
  const { platformApi, toast, i18n, formatCurrency, getCurrencyDetails } = useRedeemUx();
  const fee = Math.max(0, Number(transactionFee) || 0);
  const netFromToken =
    netAmountProp !== undefined ? Number(netAmountProp) : Math.max(0, amount - fee);
  const [redeemAmount, setRedeemAmount] = useState<number>(amount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPartialRedemption, setIsPartialRedemption] = useState(false);
  const [formattedAmount, setFormattedAmount] = useState<string>('');
  const [formattedRedeemAmount, setFormattedRedeemAmount] = useState<string>('');
  const [formattedRemainderAmount, setFormattedRemainderAmount] = useState<string>('');
  const [formattedFee, setFormattedFee] = useState<string>('');
  const [formattedNet, setFormattedNet] = useState<string>('');

  useEffect(() => {
    setIsPartialRedemption(redeemAmount < amount);
  }, [redeemAmount, amount]);

  useEffect(() => {
    const formatCurrencyValues = async () => {
      try {
        const [formatted, formattedRedeem, formattedRemainder] = await Promise.all([
          formatCurrency(amount, currency),
          formatCurrency(redeemAmount, currency),
          formatCurrency(amount - redeemAmount, currency),
        ]);
        setFormattedAmount(formatted);
        setFormattedRedeemAmount(formattedRedeem);
        setFormattedRemainderAmount(formattedRemainder);
      } catch {
        try {
          const details = await getCurrencyDetails(currency);
          const symbol = details.symbol;
          const roundedAmount = roundMonetaryValue(amount, details.decimals);
          const roundedRedeemAmount = roundMonetaryValue(redeemAmount, details.decimals);
          const roundedRemainderAmount = roundMonetaryValue(amount - redeemAmount, details.decimals);
          setFormattedAmount(`${symbol}${formatMonetaryValue(roundedAmount, details.decimals)}`);
          setFormattedRedeemAmount(`${symbol}${formatMonetaryValue(roundedRedeemAmount, details.decimals)}`);
          setFormattedRemainderAmount(`${symbol}${formatMonetaryValue(roundedRemainderAmount, details.decimals)}`);
        } catch {
          const roundedAmount = roundMonetaryValue(amount, 2);
          const roundedRedeemAmount = roundMonetaryValue(redeemAmount, 2);
          const roundedRemainderAmount = roundMonetaryValue(amount - redeemAmount, 2);
          setFormattedAmount(`${currency} ${formatMonetaryValue(roundedAmount, 2)}`);
          setFormattedRedeemAmount(`${currency} ${formatMonetaryValue(roundedRedeemAmount, 2)}`);
          setFormattedRemainderAmount(`${currency} ${formatMonetaryValue(roundedRemainderAmount, 2)}`);
        }
      }
    };

    formatCurrencyValues();
  }, [amount, redeemAmount, currency, formatCurrency, getCurrencyDetails]);

  useEffect(() => {
    const formatFeeNet = async () => {
      try {
        const [feeFmt, netFmt] = await Promise.all([
          formatCurrency(fee, currency),
          formatCurrency(netFromToken, currency),
        ]);
        setFormattedFee(feeFmt);
        setFormattedNet(netFmt);
      } catch {
        try {
          const details = await getCurrencyDetails(currency);
          const symbol = details.symbol;
          const rf = roundMonetaryValue(fee, details.decimals);
          const rn = roundMonetaryValue(netFromToken, details.decimals);
          setFormattedFee(`${symbol}${formatMonetaryValue(rf, details.decimals)}`);
          setFormattedNet(`${symbol}${formatMonetaryValue(rn, details.decimals)}`);
        } catch {
          setFormattedFee(`${currency} ${formatMonetaryValue(roundMonetaryValue(fee, 2), 2)}`);
          setFormattedNet(`${currency} ${formatMonetaryValue(roundMonetaryValue(netFromToken, 2), 2)}`);
        }
      }
    };
    formatFeeNet();
  }, [fee, netFromToken, currency, formatCurrency, getCurrencyDetails]);

  const handleRedeemFull = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await platformApi.redeemToken(tokenKey);
      toast({
        title: 'Token Redeemed Successfully!',
        description: `Full token value of ${formattedAmount} has been deposited to your account.`,
      });
      onComplete(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to redeem token';
      console.error('Full token redemption failed:', err);
      setError(msg);
      toast({
        title: 'Redemption Failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemPartial = async () => {
    if (redeemAmount <= 0 || redeemAmount > amount) {
      setError('Please enter a valid amount between $0.01 and the token value.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await platformApi.redeemToken(tokenKey, redeemAmount);
      toast({
        title: 'Token Partially Redeemed!',
        description: `${formattedRedeemAmount} has been deposited to your account. A remainder token has been created.`,
      });
      onComplete(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to redeem token';
      console.error('Partial token redemption failed:', err);
      setError(msg);
      toast({
        title: 'Redemption Failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setRedeemAmount(value);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          Token Revealed
        </CardTitle>
        <CardDescription>{i18n.__('redeemToken.redemptionPanelIntro')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isMmMoneyCurrency(currency) && (
          <RedeemMmMoneyIssuerBanner currencyKey={currency} />
        )}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <span className="text-sm text-muted-foreground">{i18n.__('redeemToken.tokenValueLabel')}</span>
            <span className="font-semibold text-lg">
              <FormattedCurrencyAmount amount={amount} currency={currency} />
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
            <span className="text-sm text-red-600">{i18n.__('redeemToken.processingFeeLabel')}</span>
            <span className="font-medium text-red-600">
              -<FormattedCurrencyAmount amount={fee} currency={currency} />
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-300">
            <span className="text-sm text-green-700">{i18n.__('redeemToken.netAmountReceivedLabel')}</span>
            <span className="font-semibold text-lg text-green-700">
              <FormattedCurrencyAmount amount={netFromToken} currency={currency} />
            </span>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span className="text-xs text-muted-foreground">{i18n.__('redeemToken.tokenCodeLabel')}</span>
            <span className="text-xs font-mono">{tokenKey}</span>
          </div>
          {message && (
            <div className="p-2 bg-blue-50 rounded border border-blue-200">
              <span className="text-sm text-blue-700 italic">&quot;{message}&quot;</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="redeemAmount" className="text-sm font-medium">
              Amount to Redeem
            </Label>
            <div className="mt-1 relative">
              <Input
                id="redeemAmount"
                type="number"
                min="0.01"
                max={amount}
                step="0.01"
                value={redeemAmount}
                onChange={handleAmountChange}
                placeholder={`Enter amount (max ${formattedAmount})`}
                className="pr-16"
              />
              {!isMmMoneyCurrency(currency) ? (
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                  {currency}
                </span>
              ) : null}
            </div>
          </div>

          {isPartialRedemption && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Partial redemption will create a remainder token worth {formattedRemainderAmount} that you can use
                later.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex flex-col gap-2 pt-4">
          <Button
            onClick={redeemAmount === amount ? handleRedeemFull : handleRedeemPartial}
            disabled={loading || redeemAmount <= 0 || redeemAmount > amount}
            className="w-full"
          >
            {loading
              ? i18n.__('redeemToken.processing')
              : redeemAmount === amount
                ? i18n.__('redeemToken.depositFullAmount')
                : i18n.__('redeemToken.depositAmount', { amount: formattedRedeemAmount })}
          </Button>
          {redeemAmount < amount && (
            <Button onClick={handleRedeemFull} disabled={loading} variant="outline" className="w-full">
              {i18n.__('redeemToken.depositFullAmount')}
            </Button>
          )}
          <Button onClick={onCancel} variant="outline" disabled={loading} className="w-full">
            {i18n.__('redeemToken.doNothing')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ManualRedemptionUI;
